import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { StatCard } from "@/components/admin/StatCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAdminDashboard } from "@/hooks/useAdminData";
import { formatPrice } from "@/lib/utils";
import { BookOpen, DollarSign, GraduationCap, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AdminDashboard() {
	const { t } = useTranslation();
	const { data, isLoading } = useAdminDashboard();

	if (isLoading) {
		return (
			<div className="space-y-8">
				<Skeleton className="h-8 w-48" />
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={`stat-${i}`} className="h-32 rounded-xl" />
					))}
				</div>
				<Skeleton className="h-64 rounded-xl" />
			</div>
		);
	}

	const stats = data;
	const publishedCourses = stats?.courses?.length ?? 0;

	return (
		<div className="space-y-8">
			<AdminBreadcrumb items={[{ label: t("admin.dashboard") }]} />

			<h1 className="text-3xl font-bold text-primary">{t("admin.dashboard")}</h1>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatCard
					icon={Users}
					label={t("admin.totalStudents")}
					value={stats?.total_students ?? 0}
				/>
				<StatCard
					icon={GraduationCap}
					label={t("admin.activeEnrollments")}
					value={stats?.active_enrollments ?? 0}
				/>
				<StatCard
					icon={DollarSign}
					label={t("admin.revenue30d")}
					value={formatPrice(stats?.revenue_30d_cents ?? 0)}
				/>
				<StatCard icon={BookOpen} label={t("admin.coursesPublished")} value={publishedCourses} />
			</div>

			{/* Course completion rates */}
			{stats?.courses && stats.courses.length > 0 && (
				<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
					<h2 className="mb-4 text-lg font-semibold text-primary">{t("admin.completionRates")}</h2>
					<div className="space-y-4">
						{stats.courses.map((course) => (
							<div key={course.id} className="space-y-1.5">
								<div className="flex items-center justify-between text-sm">
									<span className="text-primary">{course.title}</span>
									<span className="text-secondary">{course.completion_rate}%</span>
								</div>
								<div className="h-2 overflow-hidden rounded-full bg-bg-overlay">
									<div
										className="h-full rounded-full bg-accent transition-all"
										style={{
											width: `${course.completion_rate}%`,
										}}
									/>
								</div>
								<p className="text-xs text-muted">
									{course.enrollments} {t("admin.enrollmentsCount")}
								</p>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Recent enrollments */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
				<h2 className="mb-4 text-lg font-semibold text-primary">{t("admin.recentEnrollments")}</h2>
				{stats?.recent_enrollments && stats.recent_enrollments.length > 0 ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("admin.name")}</TableHead>
								<TableHead>{t("admin.email")}</TableHead>
								<TableHead>{t("admin.courseTitle")}</TableHead>
								<TableHead>{t("admin.enrolledDate")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{stats.recent_enrollments.slice(0, 10).map((enrollment) => (
								<TableRow key={enrollment.id}>
									<TableCell className="font-medium text-primary">{enrollment.name}</TableCell>
									<TableCell className="text-secondary">{enrollment.email}</TableCell>
									<TableCell className="text-primary">{enrollment.course_title}</TableCell>
									<TableCell className="text-secondary">
										{new Date(enrollment.enrolled_at).toLocaleDateString()}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<p className="py-8 text-center text-secondary">{t("admin.noRecentEnrollments")}</p>
				)}
			</div>
		</div>
	);
}
